import clsx from 'clsx';
import React from 'react';
import {
    Checkbox as ARIACheckbox,
    type CheckboxProps as AriaCheckboxProps,
} from 'react-aria-components';

import { Icon } from '@gitbook/spine-icons';

import { stopPropagationProps } from '../util';
import styles from './Checkbox.module.css';

export interface CheckboxProps extends Omit<
    AriaCheckboxProps,
    'value' | 'isDisabled' | 'isReadOnly' | 'className'
> {
    /**
     * The value of the checkbox.
     */
    value?: boolean;

    /**
     * If true, the checkbox is disabled.
     * @default false
     */
    disabled?: boolean;

    /**
     * If true, the checkbox is read-only.
     * @default false
     */
    readOnly?: boolean;

    /**
     * The size of the checkbox.
     */
    size?: 'xsmall' | 'small' | 'medium';

    /**
     * Stop pointer and click events from bubbling to parent interactive rows.
     * @default false
     */
    stopPropagation?: boolean;

    className?: string;
}

export type CheckboxRef = HTMLLabelElement;

/**
 * Checkbox component for forms with built-in accessibility support.
 */
export function Checkbox({ ref, ...props }: CheckboxProps & { ref?: React.Ref<CheckboxRef> }) {
    const {
        id,
        value,
        disabled = false,
        readOnly = false,
        onChange,
        size = 'medium',
        onKeyDown,
        stopPropagation = false,
        className,
        ...rest
    } = props;

    return (
        <ARIACheckbox
            {...rest}
            {...(stopPropagation ? stopPropagationProps : undefined)}
            ref={ref}
            id={id}
            isSelected={value}
            isDisabled={disabled}
            isReadOnly={readOnly}
            onChange={onChange}
            onKeyDown={onKeyDown}
            className={({ isSelected, isIndeterminate, isDisabled, isReadOnly }) =>
                clsx(
                    'flex shrink-0 items-center justify-center border transition-colors ease-snappy-out data-[focus-visible]:focus-ring',
                    'relative', // Ensure that when the hidden input is in a different location as the actual visual checkbox, the DOM scrolls to the correct position
                    {
                        xsmall: 'size-3.5 rounded-sm',
                        small: 'size-4 rounded-sm',
                        medium: 'size-5 rounded-md',
                    }[size],
                    isDisabled
                        ? 'cursor-not-allowed opacity-disabled'
                        : isReadOnly
                          ? 'cursor-[inherit]'
                          : 'cursor-pointer',
                    isDisabled
                        ? isSelected || isIndeterminate
                            ? 'text-neutral-text-low border-neutral-subtle bg-neutral-ui'
                            : 'text-neutral-text-low border-neutral-subtle bg-neutral-surface'
                        : isSelected || isIndeterminate
                          ? clsx(
                                'border-primary-subtle bg-primary-solid text-inverted',
                                styles.checkboxChecked
                            )
                          : 'border-neutral-subtle bg-neutral-surface hover:border-primary-interactive',
                    className
                )
            }
        >
            {({ isSelected, isIndeterminate }) =>
                isIndeterminate ? '-' : <Icon.AnimatedCheck isSelected={isSelected} />
            }
        </ARIACheckbox>
    );
}
